/**
 * Components available inside Academy articles (MDX), and the styling of plain Markdown. The build's
 * static HTML has plain versions of the same names (vite/academy-static.ts).
 *
 *   <CTA label? to? />                     button into the platform (sign-up when signed out)
 *   <KeyTakeaways> - **Lead.** text </KeyTakeaways>
 *   <ProTip title?> … </ProTip>  <Warning title?> … </Warning>  <Example title?> … </Example>
 *   <Steps><Step title="…"> … </Step></Steps>
 *   <CompareTable caption="…"> | Markdown | table | </CompareTable>
 *   <Screenshot name="order-panel" alt="…" caption? />   public/academy/img/screens/<name>.png, or a placeholder
 *   <RiskReminder />  <FAQ items={[{ q, a }]} />          the article page adds both itself
 */
import { Children, isValidElement, type AnchorHTMLAttributes, type ElementType, type HTMLAttributes, type ReactNode } from 'react';
import { BRAND } from '@platform/shared';
import { GlyphBadge, type GlyphName } from '../../../components/NotificationIcon';
import { cx, Icon } from '../../../components/ui';
import { Link } from '../../../lib/router';
import { useMe } from '../../../stores/session';
import { screenSize } from './content';
import { RISK_REMINDER } from './taxonomy';
import type { Faq } from './types';

type Kids = { children?: ReactNode };

/** Platform link: the trade screen (or `to`) when signed in, sign-up otherwise. */
export function useTradeHref(to = '/trade') {
  return useMe() ? to : '/register';
}

export function CTA({ label, to }: { label?: string; to?: string }) {
  const href = useTradeHref(to);
  return (
    <div className="my-6">
      <Link
        to={href}
        className="inline-flex h-12 items-center gap-2 rounded-full bg-accent px-6 text-[15px] font-semibold text-white no-underline shadow-[0_8px_24px_rgba(61,92,245,0.28)] transition-transform hover:-translate-y-0.5"
      >
        {label ?? `Open a trade on ${BRAND.name}`}
        <Icon name="right" size={16} />
      </Link>
    </div>
  );
}

export function KeyTakeaways({ children }: Kids) {
  return (
    <aside aria-label="Key takeaways" className="my-8 rounded-[18px] border border-line bg-panel p-5 md:p-6 [&_li]:my-2 [&_strong]:text-fg [&_ul]:my-0">
      <h2 className="m-0 mb-3 flex items-center gap-3 text-[17px] font-semibold">
        <GlyphBadge name="check" size="sm" color="var(--green)" />
        Key takeaways
      </h2>
      {children}
    </aside>
  );
}

/** A box in the article with a coloured round icon, as the app's notifications and Wallet draw them. */
function Callout({ title, glyph, color, children }: Kids & { title: string; glyph: GlyphName; color: string }) {
  return (
    <aside
      className="my-6 flex gap-4 rounded-[18px] border px-4 py-4 md:px-5"
      style={{ borderColor: `color-mix(in srgb, ${color} 32%, transparent)`, background: `color-mix(in srgb, ${color} 7%, transparent)` }}
    >
      <GlyphBadge name={glyph} size="sm" color={color} />
      <div className="min-w-0 flex-1 pt-2 [&>*:last-child]:mb-0 [&>p:first-of-type]:mt-1.5">
        <div className="text-[15.5px] leading-snug font-semibold text-fg">{title}</div>
        {children}
      </div>
    </aside>
  );
}

export const ProTip = ({ title, children }: Kids & { title?: string }) => (
  <Callout glyph="zap" color="var(--accent-text)" title={title ?? 'Pro tip'}>
    {children}
  </Callout>
);
export const Warning = ({ title, children }: Kids & { title?: string }) => (
  <Callout glyph="risk" color="var(--orange)" title={title ?? 'Warning'}>
    {children}
  </Callout>
);
export const Example = ({ title, children }: Kids & { title?: string }) => (
  <Callout glyph="chart" color="var(--managed)" title={title ? `Example: ${title}` : 'Example'}>
    {children}
  </Callout>
);

export function Steps({ children }: Kids) {
  const steps = Children.toArray(children).filter(isValidElement);
  return (
    <ol className="my-6 flex list-none flex-col gap-0 p-0">
      {steps.map((s, i) => (
        <li key={i} className="relative flex gap-4 pb-5 last:pb-0">
          {i < steps.length - 1 && <span aria-hidden="true" className="absolute top-9 bottom-1 left-[15px] w-px bg-line-strong" />}
          <span className="num grid size-8 shrink-0 place-items-center rounded-full bg-accent text-[13px] font-semibold text-white">{i + 1}</span>
          <div className="min-w-0 flex-1 pt-1 [&>*:last-child]:mb-0 [&>p:first-of-type]:mt-1">{s}</div>
        </li>
      ))}
    </ol>
  );
}

export function Step({ title, children }: Kids & { title?: string }) {
  return (
    <>
      {title && <div className="text-[16px] font-semibold text-fg">{title}</div>}
      {children}
    </>
  );
}

export function CompareTable({ caption, children }: Kids & { caption?: string }) {
  return (
    <figure className="my-7">
      {caption && <figcaption className="mb-2.5 text-[14px] font-semibold text-fg">{caption}</figcaption>}
      {children}
    </figure>
  );
}

/** Which round icon and colour stands for the screen a screenshot shows, picked from its name. */
const SCREEN_ICONS: [RegExp, GlyphName, string][] = [
  [/tp-sl|sl-line/, 'target', 'var(--green)'],
  [/margin|liquidation/, 'risk', 'var(--orange)'],
  [/leverage/, 'zap', 'var(--orange)'],
  [/fee|swap/, 'percent', 'var(--orange)'],
  [/deposit|withdraw/, 'wallet', 'var(--green)'],
  [/security|2fa|password/, 'shieldCheck', 'var(--green)'],
  [/kyc|account|register/, 'user', 'var(--accent-text)'],
  [/calendar|earnings/, 'bell', 'var(--managed)'],
  [/positions|history/, 'book', 'var(--accent-text)'],
  [/chart|timeframe/, 'chart', 'var(--managed)'],
  [/scanner|quick-view|markets/, 'pie', 'var(--managed)'],
  [/order|trade/, 'candles', 'var(--accent-text)'],
];
export const screenIcon = (name: string): [GlyphName, string] => {
  const hit = SCREEN_ICONS.find(([re]) => re.test(name));
  return hit ? [hit[1], hit[2]] : ['cap', 'var(--accent-text)'];
};

export function Screenshot({ name, alt, caption }: { name: string; alt?: string; caption?: string }) {
  const label = alt ?? name.replace(/-/g, ' ');
  const [glyph, color] = screenIcon(name);
  const size = screenSize(name);
  const src = `/academy/img/screens/${name}.png`;
  return (
    <figure className="my-7">
      {size ? (
        // Shown at half its pixel size (the captures are 2×), never wider than the column; opens full size.
        <a href={src} target="_blank" rel="noopener" title="Open the full-size screenshot" className="block">
          <img
            src={src}
            alt={label}
            width={Math.round(size[0] / 2)}
            height={Math.round(size[1] / 2)}
            loading="lazy"
            decoding="async"
            className="mx-auto block h-auto max-w-full rounded-[16px] border border-line"
          />
        </a>
      ) : (
        // Until the real screenshot is added: a window frame with the screen's icon and what it will show.
        <div
          role="img"
          aria-label={`${label} (screenshot coming soon)`}
          className="relative flex aspect-video w-full flex-col overflow-hidden rounded-[18px] border border-line bg-panel"
          style={{ backgroundImage: `radial-gradient(55% 65% at 50% 50%, color-mix(in srgb, ${color} 13%, transparent), transparent 75%)` }}
        >
          <div aria-hidden="true" className="flex items-center gap-1.5 border-b border-line px-4 py-2.5">
            <span className="size-2.5 rounded-full bg-line-strong" />
            <span className="size-2.5 rounded-full bg-line-strong" />
            <span className="size-2.5 rounded-full bg-line-strong" />
            <span className="ml-3 h-2 w-24 rounded-full bg-line" />
          </div>
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
            <GlyphBadge name={glyph} size="lg" color={color} />
            <span className="max-w-[520px] text-[15px] leading-snug font-semibold text-fg">{label}</span>
            <span className="rounded-full bg-pill px-3 py-1 text-[12px] font-medium text-muted">Screenshot coming soon</span>
            {import.meta.env.DEV && <span className="font-mono text-[11px] text-muted">{name}.png</span>}
          </div>
        </div>
      )}
      {caption && <figcaption className="mt-2.5 text-[13.5px] text-muted">{caption}</figcaption>}
    </figure>
  );
}

export function RiskReminder() {
  return (
    <aside
      aria-label="Risk reminder"
      className="my-8 flex gap-4 rounded-[18px] border p-5"
      style={{ borderColor: 'color-mix(in srgb, var(--red) 32%, transparent)', background: 'color-mix(in srgb, var(--red) 6%, transparent)' }}
    >
      <GlyphBadge name="shield" size="sm" color="var(--red)" />
      <div className="min-w-0 pt-1.5">
        <div className="text-[15.5px] font-semibold text-fg">Risk reminder</div>
        <p className="m-0 mt-1 text-[14px] leading-relaxed text-fg/85">{RISK_REMINDER}</p>
      </div>
    </aside>
  );
}

/** FAQ accordion; the article page also emits the FAQPage JSON-LD (seo.ts). */
export function FAQ({ items }: { items: readonly Faq[] }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-col divide-y divide-line overflow-hidden rounded-[16px] border border-line">
      {items.map((f, i) => (
        <details key={i} className="group bg-card open:bg-panel">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 text-[15.5px] font-semibold text-fg [&::-webkit-details-marker]:hidden">
            {f.q}
            <Icon name="chevron" size={16} className="shrink-0 text-muted transition-transform group-open:rotate-180" />
          </summary>
          <p className="m-0 px-5 pb-5 text-[15px] leading-[1.7] text-fg/85">{f.a}</p>
        </details>
      ))}
    </div>
  );
}

// ---------- Markdown elements ----------

function A({ href = '', children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement>) {
  const cls = 'font-medium text-accent-fg underline decoration-accent-fg/30 underline-offset-2 hover:decoration-accent-fg';
  if (href.startsWith('/'))
    return (
      <Link to={href} className={cls}>
        {children}
      </Link>
    );
  return (
    <a href={href} className={cls} {...(href.startsWith('#') ? {} : { target: '_blank', rel: 'noopener noreferrer' })} {...rest}>
      {children}
    </a>
  );
}

/** A Markdown element with the article styling (headings keep the id the build gave them). */
function el(Tag: ElementType, className: string) {
  return ({ className: c, ...p }: HTMLAttributes<HTMLElement>) => <Tag className={cx(className, c)} {...p} />;
}

export const MDX_COMPONENTS = {
  h2: el('h2', 'mt-12 mb-3 scroll-mt-28 text-[25px] leading-tight font-semibold tracking-tight text-fg'),
  h3: el('h3', 'mt-8 mb-2 scroll-mt-28 text-[19px] leading-snug font-semibold text-fg'),
  h4: el('h4', 'mt-6 mb-2 text-[16px] font-semibold text-fg'),
  p: el('p', 'my-4 text-[16.5px] leading-[1.75] text-fg/90'),
  ul: el('ul', 'my-4 flex list-disc flex-col gap-1.5 pl-6 text-[16.5px] leading-[1.7] text-fg/90 marker:text-muted'),
  ol: el('ol', 'my-4 flex list-decimal flex-col gap-1.5 pl-6 text-[16.5px] leading-[1.7] text-fg/90 marker:text-muted'),
  li: el('li', 'pl-1 [&>p]:my-1'),
  strong: el('strong', 'font-semibold text-fg'),
  blockquote: el('blockquote', 'my-6 border-l-[3px] border-line-strong pl-5 text-fg/80 italic'),
  hr: el('hr', 'my-10 border-0 border-t border-line'),
  code: el('code', 'num rounded-md bg-pill px-1.5 py-0.5 text-[0.88em]'),
  table: ({ children }: Kids) => (
    <div className="my-6 overflow-x-auto rounded-[14px] border border-line">
      <table className="w-full border-collapse text-left text-[14.5px]">{children}</table>
    </div>
  ),
  thead: el('thead', 'bg-panel'),
  th: el('th', 'border-b border-line px-4 py-3 align-bottom text-[13px] font-semibold whitespace-nowrap text-fg'),
  td: el('td', 'border-b border-line px-4 py-3 align-top text-fg/90 [tr:last-child>&]:border-b-0'),
  a: A,
  CTA,
  KeyTakeaways,
  ProTip,
  Warning,
  Example,
  Steps,
  Step,
  CompareTable,
  Screenshot,
  RiskReminder,
  FAQ,
};
